/**
 * onboarding-gateway.ts — contract between the journey product and the
 * onboarding overlay (ADR-002 phase C).
 *
 * journey and onboarding are both L3 products and must not import each
 * other (no-sideways). The overlay contract lives here in shared;
 * onboarding/onboarding.ts registers its implementation on module evaluation
 * (the module is loaded from the composition root), journey calls the
 * gateway.
 */

export type OnboardingAnswers = {
  fact: string;
  reaction: string;
  hypothesis: string;
  action: string;
};

export interface OnboardingRuntimeSettings {
  experience: 'static' | 'video' | 'avatar_script' | 'avatar_live';
  ab_enabled: boolean;
  video?: unknown;
  avatar?: unknown;
}

export interface OnboardingRenderCallbacks {
  onExitToCabinet: () => void;
  onDraftComplete?: (answers: OnboardingAnswers) => void | Promise<void>;
}

export interface OnboardingGateway {
  isActive(): boolean;
  hide(): void;
  render(cbs: OnboardingRenderCallbacks): void;
  getSessionId(): string;
  markSeen(): void;
  configureSessionTimeout(minutes: number | undefined): void;
  configureRuntime(config: OnboardingRuntimeSettings): void;
}

let gateway: OnboardingGateway | null = null;

/** Registered by onboarding/onboarding.ts on module evaluation. */
export function registerOnboardingGateway(impl: OnboardingGateway): void {
  gateway = impl;
}

/** No-op-safe facade: if the overlay is not mounted, the calls do nothing. */
export const onboardingOverlay = {
  isActive: (): boolean => gateway?.isActive() ?? false,
  hide: (): void => gateway?.hide(),
  render: (cbs: OnboardingRenderCallbacks): void => gateway?.render(cbs),
  getSessionId: (): string => gateway?.getSessionId() ?? '',
  markSeen: (): void => gateway?.markSeen(),
  configureSessionTimeout: (minutes: number | undefined): void =>
    gateway?.configureSessionTimeout(minutes),
  configureRuntime: (config: OnboardingRuntimeSettings): void =>
    gateway?.configureRuntime(config),
};
